import React, {
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
  useState,
  useEffect
} from "react";

// components
import Loader from "../../../../components/commonComponents/Loader/Index";
import LeftSideConversationCard from "./LeftSideConversationCard";
import RightSideConversationCard from "./RightSideConversationCard";

// assets
import StopSvg from "../../../../assets/customSVG/StopSvg";
import ArrowDown from "../../../../assets/customSVG/inbox/ArrowDown";

// utils
import moment from "moment";

// api
import { markAsRead } from "../../api/Api";

// contents
import {
  FEATURE_FLAG_BLOCKED_STATUS,
  FEATURE_FLAG_CONVERSATION_CLOSE,
  FEATURE_FLAG_CONVERSATION_OPEN,
} from "../../constants/FeatureFlag";

const closed = {
  "marketing conversation closed": "Marketing Conversation Closed",
  "marketing conversation started": "Marketing Conversation Started",
  "utility conversation closed": "Utility Conversation Closed",
  "utility conversation started": "Utility Conversation Started",
  "service conversation closed": "Service Conversation Closed",
  "service conversation started": "Service Conversation Started",
  "free entry point conversation started":
    "Free Entry Point Conversation Started",
  "free entry point conversation closed":
    "Free Entry Point Conversation Closed",
};

const getConvertedTime = (momentDate) => {
  const fromNow = momentDate.from(moment());
  if (fromNow.includes("an")) return fromNow.replace("an", "1");
  if (fromNow.includes("a ")) return momentDate.format("MMMM DD, YYYY");
  return fromNow;
};

const getConvertion = (convertedTime) => {
  if (convertedTime === moment().format("MMMM DD, YYYY")) return "Today";
  if (convertedTime === "a day ago") return "Yesterday";
  if (convertedTime === "a few seconds ago") return "now";
  return convertedTime;
};

const ConversationTabs = ({
  loading,
  conversation = [],
  conversationRef,
  bottomRef,
  setSelectedData,
  isChatResolved,
  getData,
  isFetchingPage,
  setIsFetchingPage,
  setConversationPage,
  totalMessageCount,
  scrollingBottom,
  newMessageIds,
  setNewMessageIds
}) => {
  // track previous scrollHeight
  const previousScrollHeightRef = useRef(0);

  // show/hide scroll-to-bottom button
  const [showScrollButton, setShowScrollButton] = useState(true);

  // build timeline once per conversation
  const timeline = useMemo(() => {
    const seen = new Set();
    return conversation.map((c, idx) => {
      const momentDate = moment(c.send_at);
      const convertedTime = getConvertedTime(momentDate);
      const showTime = !seen.has(convertedTime);
      if (showTime) seen.add(convertedTime);
      const convertion = getConvertion(convertedTime);
      return { c, showTime, convertedTime, convertion, idx };
    });
  }, [conversation]);

  // reply handler
  const handleReply = useCallback(
    (id) => {
      const related = conversation.find((msg) => msg.id === id);
      setSelectedData(related);
    },
    [conversation, setSelectedData]
  );

  // copy handler
  const handleCopy = useCallback(
    (id) => {
      const msg = conversation.find((m) => m.id === id);
      if (!msg) return;
      let text = "";
      const { type } = msg.message;
      if (type === "text") text = msg.message.text.body || "";
      if (type === "image") text = msg.message.image.link || "";
      if (type === "video") text = msg.message.video.link || "";
      if (type === "document") text = msg.message.document.link || "";
      if (type === "audio") text = msg.message.audio.link || "";
      if (text) navigator.clipboard.writeText(text).catch(() => {});
    },
    [conversation]
  );

  // restore scroll position when new page is loaded
  useLayoutEffect(() => {
    if (previousScrollHeightRef.current && !isFetchingPage) {
      const container = conversationRef.current;
      if (container) {
        const newH = container.scrollHeight;
        container.scrollTop = newH - previousScrollHeightRef.current;
      }
      previousScrollHeightRef.current = 0;
    }
  }, [conversation, isFetchingPage, conversationRef]);

  // onScroll: load prev page + toggle scroll-button
  const handleScroll = useCallback(() => {
    const container = conversationRef.current;
    if (!container) return;

    // load previous page when scrolled to top
    if (
      container.scrollTop <= 0 &&
      !isFetchingPage &&
      (totalMessageCount === null || conversation.length < totalMessageCount)
    ) {
      previousScrollHeightRef.current = container.scrollHeight;
      setIsFetchingPage(true);
      setConversationPage((p) => p + 1);
    }

    // show scroll-to-bottom button if not near bottom
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 50);
  }, [
    conversation.length,
    conversationRef,
    isFetchingPage,
    setConversationPage,
    setIsFetchingPage,
    totalMessageCount,
  ]);

  useEffect(() => {
    if (newMessageIds.length > 0 && !showScrollButton) {
      scrollingBottom();
    }
  }, [newMessageIds]);

  const onScrollToBottomClick = () => {
    scrollingBottom();
    setNewMessageIds([]);
  };

  return (
    <div className="w-full flex flex-1 flex-col overflow-y-scroll relative">
      {loading ? (
        <div className="w-full flex-1 flex items-center justify-center">
          <Loader Width={30} Height={30} loaderBg="white" />
        </div>
      ) : (
        <div
          className="w-full flex flex-1 flex-col overflow-y-scroll"
          ref={conversationRef}
          onScroll={handleScroll}
          style={{ willChange: "scroll-position" }}
        >
        <div
          className={`transition-opacity duration-300 ease-in-out ${
            isFetchingPage ? "opacity-100" : "opacity-0 pointer-events-none"
          } flex justify-center py-2 z-10`}
        >
          <Loader Width={30} Height={30} loaderBg="transparent" />
        </div>

          {timeline.map(({ c, showTime, convertion, idx }) => {
            const now = Date.now();
            const date =
              ["window", "assignment", "transfer_data"].includes(
                c.message.type
              ) && new Date(c.send_at) <= now
                ? new Date(c.send_at)
                : null;

            return (
              <React.Fragment key={c.id || idx}>
                {/* Date header */}
                {showTime && c.status !== "DEFAULT" && (
                  <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[white]">
                    <p className="px-[0.7vw] py-[0.25vw] rounded-full bg-[var(--font-600)] shadow-[0_1px_8px_#0000000A] text-center">
                      {convertion}
                    </p>
                  </div>
                )}

                {/* System messages */}
                {date &&
                  c.message.type === "window" &&
                  closed[c.message.text] && (
                    <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[white]">
                      <p className="w-90 px-[0.7vw] py-[0.25vw] flex items-center justify-center bg-white text-[var(--font-600)] text-center border-t border-b border-dashed border-gray-300">
                        {closed[c.message.text]}
                      </p>
                    </div>
                  )}
                  
                {/* API Trigger Indicator */}
                {c.message.type === "api_trigger" && (
                  <div className="w-full flex justify-center items-center my-2">
                    <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[#6366F1]">
                      <div className="w-90 gap-2 px-[0.7vw] py-[0.25vw] flex items-center justify-center bg-white text-[var(--font-600)] text-center border-t border-b border-dashed border-[#6366F1] text-[#6366F1]">
                        <div className="bg-[#6366F1] p-1 rounded">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                        {c.message.text}
                      </div>
                    </div>

                    {/* <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F0F4FF] border border-[#E0E7FF]">
                      <div className="bg-[#6366F1] p-1 rounded">
                        <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-[#1E40AF] mr-1">API Trigger</span>
                      {c.message.text && (
                        <span className="text-xs text-[#4B5563] truncate max-w-[200px]">{c.message.text}</span>
                      )}
                    </div> */}
                  </div>
                )}
                {
                  c.message.type === "note_attachment" &&
                  c.message.text && (
                    <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[#F6A724]">
                      <p className="w-90 px-[0.7vw] py-[0.25vw] flex items-center justify-center bg-white text-[var(--font-600)] text-center border-t border-b border-dashed border-[#F6A724] text-[#F6A724]">
                        {c.message.text}
                      </p>
                    </div>
                  )}
                {(c.message.type === "assignment" ||
                  c.message.type === "transfer_data") &&
                  date && (
                    <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[white]">
                      <p className="w-90 px-[0.7vw] py-[0.25vw] flex items-center justify-center bg-white text-[var(--font-600)] text-center border-t border-b border-dashed border-gray-300">
                        {c.message.text}
                      </p>
                    </div>
                  )}

                {/* Feature-flag open/close badges */}
                {FEATURE_FLAG_CONVERSATION_OPEN && (
                  <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[var(--contentText)]">
                    <p className="px-[0.7vw] py-[0.25vw] rounded-full bg-[var(--font-600)] shadow-[0_1px_8px_#0000000A] text-center">
                      Utility Conversation Started
                    </p>
                  </div>
                )}
                {FEATURE_FLAG_CONVERSATION_CLOSE && (
                  <div className="w-full flex justify-center items-center inbox14px-600 my-[1vw] text-[var(--contentText)]">
                    <p className="px-[0.7vw] py-[0.25vw] rounded-full bg-[var(--font-600)] shadow-[0_1px_8px_#0000000A] text-center">
                      Utility Conversation Closed
                    </p>
                  </div>
                )}

                {/* Actual message */}
                {c.is_customer_msg ? (
                  <div className="flex justify-start px-[1vw] py-[0.5vw]">
                    <LeftSideConversationCard
                      data={c}
                      handleReply={handleReply}
                      handleCopy={handleCopy}
                      isChatResolved={isChatResolved}
                    />
                  </div>
                ) : (
                  <div className="flex justify-end px-[1vw] py-[0.5vw]">
                    { !["assignment", "transfer_data", "api_trigger"].includes(c.message.type) && (
                        <RightSideConversationCard
                          data={c}
                          handleReply={handleReply}
                          handleCopy={handleCopy}
                          isChatResolved={isChatResolved}
                          getData={getData}
                        />
                      )}
                  </div>
                )}

                {/* Blocked badge */}
                {FEATURE_FLAG_BLOCKED_STATUS && (
                  <div className="w-full flex items-center justify-center">
                    <div className="w-fit bg-[#FDE68A] font-sm weight-medium flex items-center gap-2.5 p-1.5 text-[var(--contentText)] py-2.5">
                      <StopSvg width={18} height={18} />
                      <p>This user has blocked you from sending messages</p>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Scroll-to-bottom button */}
          <div
            className={`transition-all duration-300 ease-in-out transform ${
              showScrollButton
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3 pointer-events-none"
            }`}
            style={{
              position: "absolute",
              bottom: "24px",
              right: "14px",
              zIndex: 10,
            }}
          >
            <button
              onClick={onScrollToBottomClick}
              className="relative rounded-full p-[7px] flex items-center justify-center shadow-lg text-white transition-transform transform hover:scale-105"
              style={{
                background: "linear-gradient(115.45deg, #0ACD95 44.14%, #6940F2 83.89%)",
              }}
            >
              <ArrowDown height="40px" width="40px" />

              {false && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {newMessageIds.length > 99 ? "99+" : newMessageIds.length}
                </span>
              )}
            </button>
          </div>

          {/* anchor at the bottom */}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
};

export default React.memo(
  ConversationTabs,
  (prev, next) =>
    prev.conversation === next.conversation &&
    prev.loading === next.loading &&
    prev.isChatResolved === next.isChatResolved &&
    prev.isFetchingPage === next.isFetchingPage &&
    prev.newMessageIds === next.newMessageIds &&
    prev.setNewMessageIds === next.setNewMessageIds
);


// export default ConversationTabs;